import React, {useEffect, useMemo} from "react";
import {Input, Label, Offcanvas, OffcanvasBody, OffcanvasHeader, Row} from "reactstrap";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {BrokerService} from "../../../../services/broker.service";
import {useDispatch, useSelector} from "react-redux";
import {clearCountries, getCountries} from "../../../../store/countries/actions";
import {ErrorMessage, Field, Form, Formik} from 'formik';
import * as Yup from 'yup';
import {UserService} from "../../../../services/user.service";
import {TrafficOrderService} from "../../../../services/traffic_order.service";
import {buy_methods, pay_methods} from "../../../../constants/traffic_order";
import MultiDatePicker from "../../../../components/Common/MultiDatePicker";
import {CountryBoxService} from "../../../../services/country_box.service";
import moment from "moment/moment";
import useOffices from "../../../../hooks/useOffices";
import {useCrmSystems} from "../../../../hooks/useCrmSystems";
import Select from "../../../../components/Common/Select";

interface ICreateOrder {
    isOpen: boolean,
    toggle: () => void,
    crmId: number,
}

const buildValidationSchema = (isTrackbox: boolean) => Yup.object({
    broker_id: Yup.number().nullable(),
    buy_method_id: Yup.number().required('Buy method is required'),
    resale: Yup.string().when('buy_method_id', (buy_method_id) => {
        return (buy_method_id[0] === 2)
            ? Yup.string().required('Resale is required')
            : Yup.string().notRequired();
    }),
    user_ids: Yup.array().when('buy_method_id', (buy_method_id) => {
        return (buy_method_id[0] === 1)
            ? Yup.array().min(1, 'At least one team must be selected')
            : Yup.array().notRequired();
    }),
    //geo_ids: Yup.array().min(1, 'At least one country must be selected'),
    geo_ids: Yup.array()
        .nullable()
        .test(
            'geo-or-box',
            'Either geo or geo_box  must be selected',
            function (geo_ids) {
                const {geo_box_id} = this.parent;
                return (
                    (Array.isArray(geo_ids) && geo_ids.length > 0) ||
                    (geo_box_id !== null && geo_box_id !== undefined)
                );
            }
        ),
    geo_box_id: Yup.number()
        .nullable()
        .test(
            'box-or-geo',
            'Either geo box or geo must be selected',
            function (geo_box_id) {
                const {geo_ids} = this.parent;
                return (
                    (geo_box_id !== null && geo_box_id !== undefined) ||
                    (Array.isArray(geo_ids) && geo_ids.length > 0)
                );
            }
        ),
    funnel_name: Yup.string().required('Funnel name is required'),
    description: Yup.string().notRequired(),
    pay_method_id: Yup.number().required('Pay method is required'),
    payout: Yup.number().required('Payout is required').positive('Payout must be positive'),
    cr: Yup.number().when('pay_method_id', (pay_method_id) => {
        return (pay_method_id[0] === 3)
            ? Yup.number().required('CR is required').positive('CR must be positive')
            : Yup.number().notRequired();
    }),

    daily_cap: isTrackbox
        ? Yup.number().required('Daily Cap is required').positive('Daily Cap must be positive')
        : Yup.number().required('Daily Cap is required'),

    dates: Yup.array().min(1, 'At least one date must be selected'),
    from_time: Yup.string()
        .matches(/^([01]\d|2[0-3]):([0-5]\d)$/, 'Start time must be in the format HH:MM and between 00:00 and 23:59')
        .nullable()
        .required('Start time is required'),

    to_time: Yup.string()
        .matches(/^([01]\d|2[0-3]):([0-5]\d)$/, 'End time must be in the format HH:MM and between 00:00 and 23:59')
        .nullable()
        .required('End time is required'),

    office_id: Yup.number().required('Office must be selected').positive('OfficeID must be positive'),
});

const CreateOrder = ({crmId, isOpen, toggle}: ICreateOrder) => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();

    const {data: crmSystems} = useCrmSystems();

    const currentCrmSelected = crmSystems?.data.find(e => e.id === crmId);
    const isTrackbox = currentCrmSelected?.name === "Trackbox"

    const {user_offices, user_office_ids} = useOffices()
    const defaultOffice = user_office_ids.length === 1 ? user_office_ids[0] : null;
    const officeSelect =
        user_offices.map((item: any) => ({
            value: item.id,
            label: item.name,
        }))

    const {dataCountries, loading} = useSelector((state: any) => ({
        dataCountries: state.Countries.data || [],
        loading: state.Countries.loading
    }));

    const selectBrokers = (brokers: any[]) =>
        brokers.map((broker) => ({value: broker.value, label: broker.label, office_id: broker.office_id}))

    const {data: brokersSelect, isLoading: isLoadingBrokers} = useQuery({
        queryKey: ["keyBrokerList", crmId], queryFn: () => BrokerService.getBrokerByCrmIdList(crmId),
        select: selectBrokers
    });

    const {data: buyersData, isLoading: isLoadingBuyers} = useQuery({
        queryKey: ["keyBuyerTeamList"], queryFn: UserService.getBuyerTeamLead
    });

    const selectCountryBoxes = (countryBoxes: any[]) =>
        countryBoxes.map((cb) => ({value: cb.id, label: cb.name, office_id: cb.office_id}))

    const {data: countryBoxesSelect, isLoading: isLoadingCountryBoxes} = useQuery({
        queryKey: ["keyCountryBoxes"], queryFn: CountryBoxService.getCountryBoxes,
        select: selectCountryBoxes
    })

    const onSuccess = () => {
        queryClient.invalidateQueries({queryKey: ["keyTrafficOrders"]})
            .then(() => toggle())
    }

    const {
        mutate: createTrafficOrder,
        isPending: isPendingCreate
    } = useMutation({
        mutationFn: TrafficOrderService.createTrafficOrder,
        mutationKey: ['keyCreateTrafficOrder'],
        onSuccess: onSuccess
    });

    const handleSubmitForm = (values: any) => {
        createTrafficOrder(values)
    }

    useEffect(() => {
        dispatch(getCountries());
        return () => {
            dispatch(clearCountries());
        };
    }, [dispatch]);

    const initialValues = useMemo(() => ({
        broker_id: null,
        buy_method_id: null,
        resale: '',
        user_ids: [],
        geo_box_id: null,
        geo_ids: [],
        funnel_name: '',
        description: '',
        pay_method_id: null,
        payout: '',
        cr: '',
        dates: [],
        daily_cap: 0,
        from_time: "00:01",
        to_time: "23:59",
        office_id: defaultOffice,
        crm_id: crmId
    }), [crmId, defaultOffice]);

    const validationSchema = useMemo(() => buildValidationSchema(isTrackbox), [isTrackbox]);

    const handleDatesChange = (
        newDates: Date[],
        setFieldValue: (field: string, value: any) => void
    ) => {
        const sortedDates = [...newDates]
            .map(date => moment(date).format("YYYY-MM-DD"))
            .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
        setFieldValue("dates", sortedDates);
    };

    const isSaving = isPendingCreate || !!queryClient.isFetching({queryKey: ["keyTrafficOrders"]});

    return (
        <Offcanvas isOpen={isOpen} direction="end" toggle={toggle}>
            <OffcanvasHeader toggle={toggle}> Add Traffic Order </OffcanvasHeader>

            <OffcanvasBody>
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmitForm}
                    enableReinitialize={true}
                >
                    {({values, setFieldValue, isValid}) => {
                        const isSelectedOffice = !!values.office_id

                        const filteredBrokers = useMemo(() => {
                            if (!values?.office_id) return [];
                            return brokersSelect?.filter((b: any) => b.office_id === values.office_id) || [];
                        }, [values.office_id, brokersSelect]);

                        const selectedBroker = useMemo(() => {
                            if (!values?.broker_id) return null;
                            return filteredBrokers.find((b: any) => b.value === values.broker_id) || null;
                        }, [values.broker_id, filteredBrokers]);

                        return (
                            <Form>
                                <div className="mb-3">
                                    <label>Select date</label>
                                    <MultiDatePicker
                                        onDatesChange={(newDates: Date[]) => handleDatesChange(newDates, setFieldValue)}
                                    />
                                    <ErrorMessage name="dates" component="div" className="text-danger"/>
                                </div>

                                {!defaultOffice && <div className='mb-4'>
                                    <Label>Office</Label>
                                    <Select
                                        isSearchable
                                        name="office_id"
                                        label="Office"
                                        placeholder="Office"
                                        options={officeSelect}
                                        value={officeSelect.find((item: any) => item.value === values.office_id)}
                                        onChange={(value: any) => {
                                            setFieldValue('office_id', value.value);
                                            setFieldValue('broker_id', null);
                                            setFieldValue('user_ids', []);
                                            setFieldValue('geo_box_id', null);
                                        }}
                                    />
                                    <ErrorMessage name="office_id" component="div" className="text-danger"/>
                                </div>}

                                <div className="mb-4">
                                    <label>Select broker</label>
                                    <Select
                                        isClearable
                                        name="broker_id"
                                        value={selectedBroker}
                                        options={filteredBrokers}
                                        placeholder="Select broker..."
                                        isLoading={isLoadingBrokers}
                                        isDisabled={isLoadingBrokers || !isSelectedOffice}
                                        onChange={(option: any) => setFieldValue('broker_id', option ? option.value : null)}
                                    />
                                    <ErrorMessage name="broker_id" component="div" className="text-danger"/>
                                </div>

                                <div className="mb-4">
                                    <label>Select buy method</label>
                                    <Select
                                        isClearable
                                        name="buy_method_id"
                                        options={buy_methods}
                                        placeholder="Select buy method..."
                                        onChange={(option: any) => {
                                            setFieldValue('buy_method_id', option ? option.value : null);
                                            if (option?.value === 1) setFieldValue("resale", "")
                                            else setFieldValue("user_ids", [])
                                        }}
                                        value={buy_methods?.find((item) => item.value === values?.buy_method_id) || null}
                                    />
                                    <ErrorMessage name="buy_method_id" component="div" className="text-danger"/>
                                </div>

                                {values.buy_method_id === 2 && (
                                    <div className="mb-4">
                                        <label>Resale</label>
                                        <Field name="resale" as={Input} placeholder="Resale..."/>
                                        <ErrorMessage name="resale" component="div" className="text-danger"/>
                                    </div>
                                )}

                                {values.buy_method_id === 1 && (
                                    <div className="mb-4">
                                        <label>Select teams</label>
                                        <Select
                                            isMulti
                                            isClearable
                                            name="user_ids"
                                            value={buyersData?.filter((b: any) => (values?.user_ids as number[]).includes(b.value)) || []}
                                            options={buyersData?.filter((b: any) => b.office_ids.includes(values?.office_id))}
                                            placeholder="Select teams..."
                                            isLoading={isLoadingBuyers}
                                            isDisabled={isLoadingBuyers || !isSelectedOffice}
                                            onChange={(options: any) => setFieldValue('user_ids', options ? options.map((o: any) => o.value) : [])}
                                        />
                                        <ErrorMessage name="user_ids" component="div" className="text-danger"/>
                                    </div>
                                )}

                                <div className="mb-4">
                                    <label>Select order GEO BOX</label>
                                    <Select
                                        isClearable
                                        name="geo_box_id"
                                        value={countryBoxesSelect?.find((option: any) => option.value === values?.geo_box_id) || null}
                                        options={countryBoxesSelect?.filter((b: any) => b.office_id === values?.office_id)}
                                        placeholder="Select order geo box..."
                                        isLoading={isLoadingCountryBoxes}
                                        isDisabled={isLoadingCountryBoxes || !isSelectedOffice}
                                        onChange={(option: any) => setFieldValue('geo_box_id', option ? Number(option.value) : null)}
                                    />
                                    <ErrorMessage name="geo_box_id" component="div" className="text-danger"/>
                                </div>

                                <div className="mb-4">
                                    <label>Select order GEO</label>
                                    <Select
                                        isClearable
                                        name="geo_ids"
                                        isMulti
                                        options={dataCountries.map((i: any) => ({
                                            label: i.title, value: i.id
                                        }))}
                                        placeholder="Select order geo..."
                                        isLoading={loading}
                                        isDisabled={loading}
                                        onChange={(options: any) => setFieldValue('geo_ids', options ? options.map((o: any) => o.value) : [])}
                                    />
                                    <ErrorMessage name="geo_ids" component="div" className="text-danger"/>
                                </div>

                                <div className="mb-4">
                                    <label>Funnel name</label>
                                    <Field name="funnel_name" as={Input} placeholder="Funnel name..."/>
                                    <ErrorMessage name="funnel_name" component="div" className="text-danger"/>
                                </div>

                                <div className="mb-4">
                                    <label>Description</label>
                                    <Field name="description" as={Input} placeholder="Description..."
                                           type="textarea" rows={3}/>
                                    <ErrorMessage name="description" component="div" className="text-danger"/>
                                </div>

                                <div className="mb-4">
                                    <label>Select pay method</label>
                                    <Select
                                        isClearable
                                        name="pay_method_id"
                                        options={pay_methods}
                                        placeholder="Select pay method..."
                                        onChange={(option: any) => {
                                            setFieldValue('pay_method_id', option ? option.value : null)
                                            if (option?.value !== 3) setFieldValue("cr", "")
                                        }}
                                        value={pay_methods?.find((item) => item.value === values?.pay_method_id) || undefined}
                                    />
                                    <ErrorMessage name="pay_method_id" component="div" className="text-danger"/>
                                </div>

                                <Row>
                                    <div className={`mb-4 ${values.pay_method_id === 3 ? "col-8" : ""}`}>
                                        <label>Payout</label>
                                        <Field name="payout" as={Input} placeholder="Payout..."/>
                                        <ErrorMessage name="payout" component="div" className="text-danger"/>
                                    </div>
                                    {values.pay_method_id === 3 && (
                                        <div className="mb-4 col-4">
                                            <label>CR %</label>
                                            <Field name="cr" as={Input} placeholder="CR..."/>
                                            <ErrorMessage name="cr" component="div" className="text-danger"/>
                                        </div>
                                    )}
                                </Row>

                                {isTrackbox && <div className="mb-4">
                                    <label>Daily Cap</label>
                                    <Field name="daily_cap" as={Input} placeholder="Daily Cap..."/>
                                    <ErrorMessage name="daily_cap" component="div" className="text-danger"/>
                                </div>}

                                <Row>
                                    <label>Call center time</label>

                                    <div className="mb-4 col-6">
                                        <Label htmlFor="start-time-input" className="form-Label">Start Time</Label>
                                        <Field
                                            className="form-control"
                                            type="time"
                                            name="from_time"
                                            id="start-time-input"
                                            placeholder="Start Time"
                                        />
                                        <ErrorMessage name="from_time" component="div" className="text-danger"/>
                                    </div>

                                    <div className="mb-4 col-6">
                                        <Label htmlFor="to-time-input" className="form-Label">End Time</Label>
                                        <Field
                                            className="form-control"
                                            type="time"
                                            name="to_time"
                                            id="to-time-input"
                                            placeholder="End Time"
                                        />
                                        <ErrorMessage name="to_time" component="div" className="text-danger"/>
                                    </div>
                                </Row>

                                <button type="submit" className="btn btn-primary w-100"
                                        disabled={!isValid || isSaving}>
                                    {isSaving ? "Loading..." : "Save"}
                                </button>
                            </Form>
                        );
                    }}
                </Formik>
            </OffcanvasBody>

        </Offcanvas>
    );
};

export default CreateOrder;